import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// 부서 관리 페이지용 컨트롤러
// - 노드 선택 시 선택 정보 표시
// - 선택된 노드에 따라 버튼 URL 업데이트
// - 모달 열기/닫기
export default class extends Controller {
  static targets = ["addButton", "editButton", "deleteButton", "modal", "deleteModal", "deleteForm", "errorAlert", "productMoveModal", "selectHint"]
  static values = {
    addUrl: String,
    editUrl: String,
    deleteUrl: String,
    checkDeletableUrl: String,
    checkHasProductsUrl: String
  }

  connect() {
    this.selectedNodeId = null
    this.selectedNodeDepth = null
  }

  // 하위부서 생성 모달 열기
  async openAddModal(event) {
    event.preventDefault()
    if (!this.selectedNodeId) return

    const isVirtualRoot = this.selectedNodeId === '__workplace_root__'

    // 가상 루트 선택 시: 바로 생성 폼 열기
    if (isVirtualRoot) {
      this.openModalWithUrl(this.addUrlValue)
      return
    }

    // 선택한 부서에 제품이 있는지 확인
    if (this.hasCheckHasProductsUrlValue) {
      try {
        const checkUrl = this.checkHasProductsUrlValue.replace(':code', this.selectedNodeId)
        const response = await fetch(checkUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (data.has_products) {
            // 제품이 있어도 막지 않는다 — 하위를 만들면서 서버가 제품을 그 아래로
            // 내린다. 위치가 바뀌는 일이라 먼저 확인만 받는다.
            this.openProductMoveModal(data.product_count)
            return
          }
        }
      } catch (error) {
        console.error('Failed to check products:', error)
      }
    }

    // 제품 없음: 바로 생성 폼 열기
    const url = `${this.addUrlValue}?parent_code=${this.selectedNodeId}`
    this.openModalWithUrl(url)
  }

  // 부서 수정 모달 열기
  openEditModal(event) {
    event.preventDefault()
    // 최상위 부서(depth=0) 또는 가상 루트는 수정 불가
    const isRootLevel = this.selectedNodeId === '__workplace_root__' || this.selectedNodeDepth === 0
    if (!this.selectedNodeId || isRootLevel) return

    const url = this.editUrlValue.replace(':code', this.selectedNodeId)
    this.openModalWithUrl(url)
  }

  // 부서 삭제 확인 모달 열기
  async openDeleteModal(event) {
    event.preventDefault()
    // 최상위 부서(depth=0) 또는 가상 루트는 삭제 불가
    const isRootLevel = this.selectedNodeId === '__workplace_root__' || this.selectedNodeDepth === 0
    if (!this.selectedNodeId || isRootLevel) return

    if (!this.hasDeleteModalTarget) return

    // 삭제 가능 여부 확인 (제품 등록 여부 체크)
    if (this.hasCheckDeletableUrlValue) {
      try {
        const checkUrl = this.checkDeletableUrlValue.replace(':code', this.selectedNodeId)
        const response = await fetch(checkUrl, {
          headers: {
            'Accept': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
          }
        })

        if (response.ok) {
          const data = await response.json()
          if (!data.deletable) {
            // 삭제 불가 - 에러 알림 표시
            this.openErrorAlert()
            return
          }
        }
      } catch (error) {
        console.error('Failed to check deletability:', error)
      }
    }

    // 삭제 폼 URL 업데이트
    if (this.hasDeleteFormTarget && this.hasDeleteUrlValue) {
      const url = this.deleteUrlValue.replace(':code', this.selectedNodeId)
      this.deleteFormTarget.setAttribute('action', url)
    }

    // 삭제 확인 모달 열기
    const modalElement = this.deleteModalTarget.querySelector('[data-controller="modal"]')
    if (modalElement) {
      const modalController = this.application.getControllerForElementAndIdentifier(modalElement, 'modal')
      if (modalController) {
        modalController.open()
      }
    }
  }

  // 에러 알림 열기
  openErrorAlert() {
    if (!this.hasErrorAlertTarget) return

    const alertElement = this.errorAlertTarget.querySelector('[data-controller="alert"]')
    if (alertElement) {
      const alertController = this.application.getControllerForElementAndIdentifier(alertElement, 'alert')
      if (alertController) {
        alertController.open()
      }
    }
  }

  // 제품 이동 확인 모달 열기
  openProductMoveModal(productCount) {
    if (!this.hasProductMoveModalTarget) return

    const countEl = this.productMoveModalTarget.querySelector('[data-product-move="count"]')
    if (countEl) countEl.textContent = productCount ?? ''

    const modalElement = this.productMoveModalTarget.querySelector('[data-controller="modal"]')
    if (!modalElement) return

    const modalController = this.application.getControllerForElementAndIdentifier(modalElement, 'modal')
    if (!modalController) return

    modalController.open()
    if (modalController.hasConfirmButtonTarget) {
      modalController.confirmButtonTarget.disabled = false
    }
  }

  // 확인 → 생성 폼으로 진행. 이동은 서버가 부서 생성과 한 트랜잭션으로 처리한다.
  confirmProductMove() {
    if (!this.selectedNodeId) return

    this.openModalWithUrl(`${this.addUrlValue}?parent_code=${this.selectedNodeId}`)
  }

  // 삭제 확인 시 실제 삭제 수행
  confirmDelete(event) {
    if (this.hasDeleteFormTarget) {
      this.deleteFormTarget.requestSubmit()
    }
  }

  // 모달 열기 공통 함수
  openModalWithUrl(url) {
    if (!this.hasModalTarget) return

    // Turbo Frame으로 내용 로드
    const frame = this.modalTarget.querySelector('turbo-frame')
    if (frame) {
      frame.src = url
      frame.reload()
    }

    // modal 컨트롤러의 open 메서드 호출
    const modalController = this.application.getControllerForElementAndIdentifier(this.modalTarget, 'modal')
    if (modalController) {
      modalController.open()
    }
  }

  showSelected(event) {
    const { nodeId, nodeDepth } = event.detail
    if (!nodeId) {
      this.hideSelected()
      return
    }

    this.selectedNodeId = nodeId
    this.selectedNodeDepth = nodeDepth
    this.updateButtonUrls(nodeId, nodeDepth)
    if (this.hasSelectHintTarget) this.selectHintTarget.classList.add('hidden')
  }

  hideSelected() {
    this.selectedNodeId = null
    this.selectedNodeDepth = null
    this.updateButtonUrls(null, null)
    if (this.hasSelectHintTarget) this.selectHintTarget.classList.remove('hidden')
  }

  updateButtonUrls(nodeId, nodeDepth) {
    // button_to with block은 data 속성을 button에 적용
    // target은 button이고, form은 button.closest('form')으로 찾음

    const enableButton = (button) => {
      if (!button) return
      button.style.backgroundColor = '#1F2A6D'  // primary-900
      button.style.color = '#ffffff'
      button.style.opacity = '1'
      button.classList.remove('pointer-events-none')
    }

    const disableButton = (button) => {
      if (!button) return
      button.style.backgroundColor = '#1F2A6D'  // primary-900 (same as enabled)
      button.style.color = '#ffffff'
      button.style.opacity = '0.5'
      button.classList.add('pointer-events-none')
    }

    const isWorkplaceRoot = nodeId === '__workplace_root__'
    // 최상위 부서 (depth === 0) 또는 가상 루트는 수정/삭제 불가
    const isRootLevel = isWorkplaceRoot || nodeDepth === 0

    // 하위부서 생성 버튼 URL 업데이트
    // root 선택 시: 최상위 부서 생성 (parent_code 없음)
    // 부서 선택 시: 해당 부서 아래 하위부서 생성
    if (this.hasAddButtonTarget && this.hasAddUrlValue) {
      const button = this.addButtonTarget
      const form = button.closest('form')
      if (nodeId) {
        const url = isRootLevel
          ? this.addUrlValue
          : `${this.addUrlValue}?parent_code=${nodeId}`
        if (form) form.setAttribute('action', url)
        enableButton(button)
      } else {
        if (form) form.setAttribute('action', '#')
        disableButton(button)
      }
    }

    // 부서 수정 버튼 URL 업데이트
    // 최상위 부서는 수정 불가
    if (this.hasEditButtonTarget && this.hasEditUrlValue) {
      const button = this.editButtonTarget
      const form = button.closest('form')
      if (nodeId && !isRootLevel) {
        if (form) form.setAttribute('action', this.editUrlValue.replace(':code', nodeId))
        enableButton(button)
      } else {
        if (form) form.setAttribute('action', '#')
        disableButton(button)
      }
    }

    // 부서 삭제 버튼 활성화/비활성화
    // 최상위 부서는 삭제 불가
    if (this.hasDeleteButtonTarget) {
      const button = this.deleteButtonTarget
      if (nodeId && !isRootLevel) {
        enableButton(button)
      } else {
        disableButton(button)
      }
    }
  }
}
